import { mobileAccess, processViewPartHtml, virtualDir } from "#skyward_global";
import { ButtonDefinition, ModalDialog, showDialog } from "#skyward_modalLayer";
import { loadContent, setSkipUnloadCheck } from "#skyward_window";
import { disable as disableIdleMonitor, startWarningTimers } from "./skyward.base.idleMonitor";
import { getDisplayTimespan, now, setCurrentTime } from "./skyward.base.time";

export enum SessionState
{
    Active = "Active",
    Inactive = "Inactive",
    ClientTimedOut = "ClientTimedOut",
    ClientTimedOut_Posted = "ClientTimedOut_Posted"
}

let isEnabled = false;
export function enabled()
{
    return isEnabled;
}

let postTimer: NodeJS.Timeout | undefined;
const DEFAULT_POST_INTERVAL = 5000;
let postInterval = DEFAULT_POST_INTERVAL;
let currentStateVariable: SessionState = SessionState.Active;

const noticeBarContainer = $(".systemwideMessageBar");

const systemNoticeBar = noticeBarContainer.find("#systemNoticeNoticeBar");
let hasSeenUpdateMessage = systemNoticeBar.data("hasseenupdatemessage") || false;
const systemNoticebarData = systemNoticeBar.data();
const receivedTime = Date.now();
let lockTime = systemNoticebarData?.utcLockTime ? new Date(systemNoticebarData.utcLockTime).getTime() : null;
let serverTime = systemNoticebarData?.utcServerTime ? new Date(systemNoticebarData.utcServerTime).getTime() : receivedTime;
let serverTimeOffset = serverTime - receivedTime;
let countdownTimer: NodeJS.Timeout | undefined;
let timerForAlteredTime: NodeJS.Timeout | null = null;

const environmentPurposeBar = noticeBarContainer.find("#environmentPurposeNoticeBar");
const trainingModeBar = noticeBarContainer.find("#trainingModeNoticeBar");
const alteredTimeBar = noticeBarContainer.find("#alteredTimeNoticeBar");

countdownTimer = setTimeout(decrementRemainingSecondsUntilLock, 1000);

let lastTransmissionNumber = 0;

export function currentState()
{
    return currentStateVariable;
}

export function enable(interval?: number | null)
{
    if (postTimer)
    {
        clearTimeout(postTimer);
    }

    postInterval = interval || DEFAULT_POST_INTERVAL;
    currentStateVariable = SessionState.Inactive;
    postTimer = setTimeout(postState, postInterval);
    isEnabled = true;
}

export function disable()
{
    clearTimeout(postTimer);
    isEnabled = false;
}

export function setState(newState: SessionState)
{
    if (isEnabled)
    {
        currentStateVariable = newState;
    }
}

export function postState(state?: SessionState)
{
    clearTimeout(postTimer);
    if (currentStateVariable == SessionState.ClientTimedOut_Posted)
    {
        return;
    }

    currentStateVariable = state || currentStateVariable;
    lastTransmissionNumber++;
    if (currentStateVariable == SessionState.Active)
    {
        $.ajax(
        {
            url: `${virtualDir}Security/Session/Active`,
            type: "POST",
            dataType: "JSON",
            activityIndicator: { silent: true },
            data: { transmissionNumber: lastTransmissionNumber, hasSeenUpdateMessage: hasSeenUpdateMessage },
            global: false,
            success: handleSessionPingResponse,
            error: function () { sessionHasEnded("ServerError") }
        });
    }
    else if (currentStateVariable == "Inactive")
    {
        $.ajax(
        {
            url: `${virtualDir}Security/Session/Inactive`,
            type: "POST",
            dataType: "JSON",
            activityIndicator: { silent: true },
            data: { transmissionNumber: lastTransmissionNumber, hasSeenUpdateMessage: hasSeenUpdateMessage },
            global: false,
            success: handleSessionPingResponse,
            error: function () { sessionHasEnded("Inactive") }
        });
    }
    else if (currentStateVariable == "ClientTimedOut")
    {
        // This prevents double-posts by stopping any additional timers in idleMonitor.ts
        disableIdleMonitor();

        $.ajax(
        {
            url: `${virtualDir}Security/Session/ClientTimedOut`,
            type: "POST",
            activityIndicator: { silent: true },
            data: { transmissionNumber: lastTransmissionNumber },
            global: false,
            success: function ()
            {
                if (!mobileAccess)
                {
                    sessionHasEnded("TimedOut");
                }
            },
            error: function ()
            {
                if (!mobileAccess)
                {
                    sessionHasEnded("TimedOut");
                }
            }
        });
        return;
    }
    postTimer = setTimeout(postState, postInterval);
    currentStateVariable = SessionState.Inactive;
}

function decrementRemainingSecondsUntilLock()
{
    clearTimeout(countdownTimer);
    if (lockTime)
    {
        $(systemNoticeBar).find(".lockCountdown").html(getDisplayTimespan(lockTime - getCurrentTime()));

        updateNoticebar(systemNoticeBar, { html: $(systemNoticeBar).html() });
        countdownTimer = setTimeout(decrementRemainingSecondsUntilLock, 1000);
    }
}
function getCurrentTime()
{
    return new Date(Date.now() + serverTimeOffset).getTime();
}

function sessionHasEnded(htmlErrorText: string)
{
    setSkipUnloadCheck(true);
    window.location.href = `${virtualDir}Security/SignIn/NotAuthenticated?status=${htmlErrorText}&cont=${encodeURIComponent(window.location.href) }`;
}

interface SessionPingResponse
{
    transmissionNumber: number;
    status: string;
    utcLockTime: string | null;
    hasSeenUpdateMessage: boolean;
    lockMessageContent: { html: string } | null;
    trainingMode: boolean;
    trainingTimeOffset: number | null;
    currentTime: number;
    offset?: number;
    environmentPurpose?: { html: string, css?: string[] };
}

async function handleSessionPingResponse(data: SessionPingResponse)
{
    if (data.transmissionNumber < lastTransmissionNumber)
    {
        return;
    }
    if (data.status !== "Active")
    {
        if (!mobileAccess)
        {
            sessionHasEnded(data.status);
        }
    }

    const utcDate = data.utcLockTime;
    lockTime = utcDate ? new Date(utcDate).getTime() : null;
    hasSeenUpdateMessage = data.hasSeenUpdateMessage;
    if (data.lockMessageContent && lockTime)
    {
        const barContent = await $("<div />").viewpartHtml(data.lockMessageContent.html);
        barContent.find(".lockCountdown").html(getDisplayTimespan(lockTime - getCurrentTime()));
        updateNoticebar(systemNoticeBar, { html: barContent.html() });
    }
    else
    {
        updateNoticebar(systemNoticeBar);
    }
    updateNoticebar(trainingModeBar, data.trainingMode ? { html: "System is in Training Mode" } : undefined);
    updateNoticebar(alteredTimeBar, data.trainingTimeOffset != null ? { html: "System time has been altered" } : undefined);
    displayAlteredTime();
    if (data.environmentPurpose)
    {
        const processedEnvHtml = await processViewPartHtml(data.environmentPurpose.html) as string;
        updateNoticebar(environmentPurposeBar, { html: processedEnvHtml, css: data.environmentPurpose.css });
    }
    else
    {
        updateNoticebar(environmentPurposeBar);
    }

    if (data.lockMessageContent)
    {
        if (lockTime != null)
        {
            clearTimeout(countdownTimer);
            countdownTimer = setTimeout(decrementRemainingSecondsUntilLock, 1000);

            if (!hasSeenUpdateMessage)
            {
                const options = { closeOnClick: false, closeOnEscape: false };

                let updatePendingModal: ModalDialog;

                const buttons: ButtonDefinition[] =
                [
                    { text: "Sign Out Now", icon: "SignOut", click: function () { updatePendingModal.close(); loadContent("full", "security", "SignIn", "index", null, undefined); } },
                    { text: "Keep Working", icon: "SignIn", click: "close" }
                ];

                updatePendingModal = showDialog("Update Pending", contents, buttons, options);
            }
        }
        enable(10000);
    }

    if (!mobileAccess)
    {
        startWarningTimers(data.offset);
    }

    setCurrentTime(data.currentTime);

    function contents()
    {
        hasSeenUpdateMessage = true; //todo: send to save immediately instead?
        const result = $("<div />").html(systemNoticeBar.html());
        setInterval(function ()
        {
            result.html(systemNoticeBar.html());
        }, 1000);
        return result;
    }
}

function displayAlteredTime()
{
    alteredTimeBar.html("<span>System time has been altered; the effective time is <span id='alteredTimeBarTimeDisplay'></span></span>");

    if (timerForAlteredTime != null)
    {
        clearTimeout(timerForAlteredTime);
        timerForAlteredTime = null;
    }

    const displayElement = $("#alteredTimeBarTimeDisplay");

    if (alteredTimeBar.is(":visible"))
    {
        updateDisplay();
        timerForAlteredTime = setInterval(updateDisplay, 1000);
    }

    function updateDisplay()
    {
        displayElement.html(now().toLocaleString());
    }
}

function updateNoticebar(message: JQuery<HTMLElement>, args?: { html: string, css?: string[] })
{
    if (args)
    {
        message.html(args.html);
        message.css(args.css || []);
        message.addClass("noticeBar");
        message.removeClass("noticeBar--hidden");
    }
    else
    {
        message.addClass("noticeBar--hidden");
    }
}

function getLockTime(noticeBar: JQuery<HTMLElement>)
{
    const data = noticeBar.data();
    if (!data)
    {
        return null;
    }
    const date = data.utcLockTime;
    return date ? new Date(date) : null;
}

$(function ()
{
    displayAlteredTime();

    if (systemNoticeBar.length && systemNoticeBar[0].hasAttribute("data-locktime"))
    {
        postState();
    }
});
